import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  NgZone,
  OnDestroy,
  PLATFORM_ID,
  inject,
} from '@angular/core';
import {
  animate,
  query,
  stagger,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { RouterModule } from '@angular/router';
import { AngularFireDatabase } from '@angular/fire/compat/database';
import { Subscription } from 'rxjs';

/** The three buckets the Firebase payload ships, plus the catch-all tab. */
export type PfCategory = 'Website' | 'Mobile' | 'Social Media';
export type PfFilter = 'All' | PfCategory;

/** Normalised shape the template renders — the raw records differ per bucket. */
interface PfProject {
  title: string;
  details: string;
  image: string;
  category: PfCategory;
  link: string;
}

interface PfTab {
  id: PfFilter;
  label: string;
}

@Component({
  selector: 'app-our-portfolio',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './our-portfolio.component.html',
  styleUrl: './our-portfolio.component.scss',
  animations: [
    // Stagger the grid on first paint and on every filter switch.
    trigger('pfGrid', [
      transition('* => *', [
        query(
          ':enter',
          [
            style({ opacity: 0, transform: 'translateY(26px) scale(0.97)' }),
            stagger(70, [
              animate(
                '620ms cubic-bezier(0.22, 1, 0.36, 1)',
                style({ opacity: 1, transform: 'none' })
              ),
            ]),
          ],
          { optional: true }
        ),
      ]),
    ]),
  ],
})
export class OurPortfolioComponent implements AfterViewInit, OnDestroy {
  private db = inject(AngularFireDatabase);
  private platformId = inject(PLATFORM_ID);
  private cdr = inject(ChangeDetectorRef);
  private host = inject<ElementRef<HTMLElement>>(ElementRef);
  private zone = inject(NgZone);

  readonly isBrowser = isPlatformBrowser(this.platformId);

  readonly tabs: PfTab[] = [
    { id: 'All', label: 'All' },
    { id: 'Website', label: 'Website' },
    { id: 'Mobile', label: 'Mobile' },
    { id: 'Social Media', label: 'Social Media Management' },
  ];

  activeFilter: PfFilter = 'All';

  /** Every visible project across the three buckets, in tab order. */
  projects: PfProject[] = [];
  visibleProjects: PfProject[] = [];
  loading = true;

  /** Reveal-on-scroll only runs client-side, so SSR output stays visible. */
  @HostBinding('class.pf-anim') animated = false;

  private sub?: Subscription;
  private observer?: IntersectionObserver;

  constructor() {
    // On the server the data never resolves, so the skeletons stay — the
    // browser fills them in on hydration.
    if (!this.isBrowser) return;

    this.animated = !this.prefersReducedMotion();

    this.sub = this.db
      .object('/')
      .valueChanges()
      .subscribe({
        next: (data: any) => {
          this.projects = [
            ...this.normalise(data?.webCard, 'Website'),
            ...this.normalise(data?.mobilecards, 'Mobile'),
            ...this.normalise(data?.ssmCards, 'Social Media'),
          ];
          this.loading = false;
          this.applyFilter(this.activeFilter);
          this.cdr.markForCheck();
          // Cards arrive after the first paint — reveal the ones just added.
          setTimeout(() => this.observeReveals());
        },
        error: (err) => {
          console.error('Portfolio: Firebase subscription failed', err);
          this.loading = false;
          this.cdr.markForCheck();
        },
      });
  }

  ngAfterViewInit(): void {
    this.observeReveals();
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    this.observer?.disconnect();
  }

  setFilter(id: PfFilter): void {
    if (this.activeFilter === id) return;
    this.applyFilter(id);
  }

  /** Roving-tabindex keyboard support for the pill switcher. */
  onTabKeydown(event: KeyboardEvent, index: number): void {
    const last = this.tabs.length - 1;
    let next = index;

    switch (event.key) {
      case 'ArrowRight':
        next = index === last ? 0 : index + 1;
        break;
      case 'ArrowLeft':
        next = index === 0 ? last : index - 1;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = last;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.setFilter(this.tabs[next].id);

    if (this.isBrowser) {
      const pills = this.host.nativeElement.querySelectorAll<HTMLElement>('.pf-tabs__pill');
      pills[next]?.focus();
    }
  }

  /**
   * Animation state for the grid. It folds the row count in so the stagger also
   * plays when the async Firebase payload lands, not only on filter switches.
   */
  get gridState(): string {
    return `${this.activeFilter}:${this.visibleProjects.length}`;
  }

  trackByProject(_i: number, project: PfProject): string {
    return `${project.category}-${project.title}-${project.image}`;
  }

  private applyFilter(id: PfFilter): void {
    this.activeFilter = id;
    this.visibleProjects =
      id === 'All' ? this.projects : this.projects.filter((p) => p.category === id);
  }

  private normalise(raw: any, category: PfCategory): PfProject[] {
    if (!Array.isArray(raw)) return [];

    return raw
      .filter((card) => card?.visibility)
      .map((card) => ({
        // web / mobile records spell it `tittle`, the social ones `title`
        title: (card.tittle ?? card.title ?? '').toString().trim(),
        details: (card.details ?? '').toString().trim(),
        image: card.image ?? '',
        category,
        link: card.link ?? card.url ?? '',
      }))
      .filter((card) => !!card.image);
  }

  /** Fades sections in as they enter the viewport — transform/opacity only. */
  private observeReveals(): void {
    if (!this.isBrowser || !this.animated) return;

    const targets = Array.from(
      this.host.nativeElement.querySelectorAll<HTMLElement>('[data-pf-reveal]:not(.is-in)')
    );
    if (!targets.length) return;

    if (!('IntersectionObserver' in window)) {
      targets.forEach((el) => el.classList.add('is-in'));
      return;
    }

    this.observer ??= new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          this.observer?.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.12 }
    );

    this.zone.runOutsideAngular(() => targets.forEach((el) => this.observer!.observe(el)));
  }

  private prefersReducedMotion(): boolean {
    return (
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }
}
